import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Dns as DataIcon,
  Hub as KeeperIcon,
  FiberManualRecord as DotIcon,
} from '@mui/icons-material';

const STATUS_COLOR = {
  RUNNING: 'success',
  STOPPED: 'default',
  DEPLOYING: 'warning',
  PENDING: 'default',
  ERROR: 'error',
};

/**
 * Read-only visualization of a ClickHouse cluster's physical topology.
 *
 * <p>Groups assigned nodes by role (DATA vs KEEPER), then — for DATA —
 * by {@code shardNum} (fallback 1 when unset). Each replica within a
 * shard is shown as a card; empty slots (when the cluster\'s declared
 * {@code replicasPerShard} exceeds the count of assigned replicas) are
 * rendered as placeholder cards so the topology holes are visible.
 *
 * <p>Designed to stay useful for single-node clusters (renders a single
 * shard with one replica) AND multi-shard / multi-replica production
 * clusters (a wider grid). Keeper nodes get their own row since they\'re
 * orthogonal to sharding.
 */
const ClickHouseTopologyDiagram = ({ cluster, nodes }) => {
  const { shards, keepers, dataCount } = useMemo(() => {
    const list = Array.isArray(nodes) ? nodes : [];
    // Combined-role (DATA_AND_KEEPER) nodes serve BOTH — they must appear in a
    // shard AND the keeper row, or the diagram silently drops them and shows
    // wrong counts.
    const dataNodes = list.filter(n => ['DATA', 'DATA_AND_KEEPER'].includes((n.nodeRole || 'DATA').toUpperCase()));
    const keeperNodes = list.filter(n => ['KEEPER', 'DATA_AND_KEEPER'].includes((n.nodeRole || '').toUpperCase()));
    // Group DATA nodes by shard.
    const byShard = new Map();
    for (const n of dataNodes) {
      const s = n.shardNum ?? 1;
      if (!byShard.has(s)) byShard.set(s, []);
      byShard.get(s).push(n);
    }
    const declaredShards = cluster?.shardCount || Math.max(1, byShard.size || 1);
    const replicasPer = cluster?.replicasPerShard || Math.max(1,
        Math.max(...Array.from(byShard.values()).map(v => v.length), 1));
    const shardArr = [];
    for (let s = 1; s <= declaredShards; s++) {
      const assigned = (byShard.get(s) || []).slice().sort(
          (a, b) => (a.replicaName || '').localeCompare(b.replicaName || ''));
      const slots = [];
      for (let r = 0; r < Math.max(replicasPer, assigned.length); r++) {
        slots.push(assigned[r] || null);
      }
      shardArr.push({ shardNum: s, slots });
    }
    return { shards: shardArr, keepers: keeperNodes, dataCount: dataNodes.length };
  }, [cluster, nodes]);

  return (
    <Box sx={{ py: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle2">
          {cluster?.name || 'Cluster'}
        </Typography>
        <Chip size="small" variant="outlined"
              label={`${shards.length} shard${shards.length === 1 ? '' : 's'} × ${cluster?.replicasPerShard ?? '?'} replica${cluster?.replicasPerShard === 1 ? '' : 's'}`} />
        <Chip size="small" variant="outlined"
              label={`${dataCount} DATA node${dataCount === 1 ? '' : 's'}`}
              icon={<DataIcon sx={{ fontSize: 14 }} />} />
        <Chip size="small" variant="outlined"
              label={`${keepers.length} KEEPER node${keepers.length === 1 ? '' : 's'}`}
              icon={<KeeperIcon sx={{ fontSize: 14 }} />} />
      </Stack>

      {/* Shard rows */}
      <Stack spacing={1.5}>
        {shards.map(({ shardNum, slots }) => (
          <Paper key={shardNum} variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 72 }}>
                Shard {shardNum}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Typography variant="caption" color="text.secondary">
                {slots.filter(Boolean).length} / {slots.length} replicas assigned
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {slots.map((node, idx) => <ReplicaCard key={idx} node={node} slotIndex={idx} />)}
            </Stack>
          </Paper>
        ))}

        {/* Keeper row */}
        {keepers.length > 0 && (
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(25,118,210,0.08)' : 'rgba(25,118,210,0.04)' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <KeeperIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Keeper quorum
              </Typography>
              {keepers.length < 3 && (
                <Tooltip title="Keeper quorum should be an odd number of ≥3 nodes for production use. Fewer = no fault tolerance for coordination failures.">
                  <Chip size="small" color="warning"
                        label="quorum < 3 — not fault-tolerant" />
                </Tooltip>
              )}
              {keepers.length >= 3 && keepers.length % 2 === 0 && (
                <Tooltip title="Keeper quorum must be an odd number. An even count gives you no extra fault tolerance over (N-1).">
                  <Chip size="small" color="warning" label="even count — should be odd" />
                </Tooltip>
              )}
            </Stack>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {keepers.map(n => <ReplicaCard key={n.id} node={n} isKeeper />)}
            </Stack>
          </Paper>
        )}

        {keepers.length === 0 && (cluster?.replicasPerShard ?? 1) > 1 && (
          <Paper variant="outlined" sx={{ p: 1.5, borderColor: 'warning.main' }}>
            <Typography variant="body2" color="warning.main">
              ⚠ Topology declares <strong>{cluster.replicasPerShard}</strong> replicas per shard but no KEEPER nodes are assigned.
              Replicated tables need Keeper to coordinate replication — assign keeper nodes before deploying, or schema init will fall back to plain MergeTree.
            </Typography>
          </Paper>
        )}
      </Stack>
    </Box>
  );
};

/** One card per replica slot. Null slot = empty/unassigned. */
const ReplicaCard = ({ node, slotIndex, isKeeper }) => {
  if (!node) {
    return (
      <Paper variant="outlined" sx={{
        minWidth: 200, p: 1.25,
        borderStyle: 'dashed',
        borderColor: 'divider',
        opacity: 0.65,
      }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <DotIcon sx={{ fontSize: 10, color: 'text.disabled' }} />
          <Typography variant="caption" color="text.secondary">
            replica_{slotIndex + 1} — unassigned
          </Typography>
        </Stack>
      </Paper>
    );
  }

  const statusColor = STATUS_COLOR[node.status] || 'default';
  const diskUsage = node.diskTotalGb && node.diskFreeGb != null
    ? `${node.diskTotalGb - node.diskFreeGb}/${node.diskBudgetGb || node.diskTotalGb} GB`
    : null;

  return (
    <Paper variant="outlined" sx={{
      minWidth: 220, p: 1.25,
      borderColor: (t) => (statusColor === 'error' ? t.palette.error.main
                         : statusColor === 'warning' ? t.palette.warning.main
                         : 'divider'),
    }}>
      <Stack spacing={0.5}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {isKeeper
            ? <KeeperIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            : <DataIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {node.name || 'node'}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Chip size="small" color={statusColor} label={node.status || '?'}
                sx={{ height: 18, fontSize: 10 }} />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {node.host}{node.clickhouseHttpPort ? `:${node.clickhouseHttpPort}` : ''}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
          {node.replicaName && (
            <Chip size="small" variant="outlined"
                  label={`replica=${node.replicaName}`}
                  sx={{ fontSize: 10, height: 18 }} />
          )}
          {!isKeeper && node.shardNum != null && (
            <Chip size="small" variant="outlined"
                  label={`shard=${node.shardNum}`}
                  sx={{ fontSize: 10, height: 18 }} />
          )}
          {isKeeper && node.keeperServerId != null && (
            <Chip size="small" variant="outlined"
                  label={`server_id=${node.keeperServerId}`}
                  sx={{ fontSize: 10, height: 18 }} />
          )}
          {node.version && (
            <Chip size="small" variant="outlined"
                  label={`v${node.version}`}
                  sx={{ fontSize: 10, height: 18 }} />
          )}
          {diskUsage && (
            <Chip size="small" variant="outlined"
                  label={diskUsage + (node.diskBudgetGb ? ' budget' : ' used')}
                  sx={{ fontSize: 10, height: 18 }} />
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

export default ClickHouseTopologyDiagram;
